import React from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { GuideAccordionList } from '@backrs_ui/grow-ui/src/components/GuidePage';
import type { CommonAppEssayPrompt } from '@backrs_ui/grow-ui/src/pages/CommonAppEssayGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

const strings = appStrings.modules.commonAppEssayGuide.prompts;

type PromptsSectionProps = Readonly<{
  prompts: readonly CommonAppEssayPrompt[];
}>;

export const PromptsSection: React.FC<PromptsSectionProps> = ({ prompts }) => (
  <GuideAccordionList
    idPrefix="common-app-essay-prompt"
    items={prompts.map((prompt) => ({
      id: prompt.id,
      title: prompt.title,
      chipLabel: `${strings.promptLabel} ${prompt.number}`,
      details: <PromptDetails prompt={prompt} />,
    }))}
  />
);

const PromptDetails: React.FC<{ prompt: CommonAppEssayPrompt }> = ({ prompt }) => (
  <Box sx={detailsOuterSx}>
    <Box sx={detailsContainerSx}>
      <Box sx={promptCardSx}>
        <Typography variant="text-md-regular" sx={promptTextSx}>
          {prompt.promptText}
        </Typography>
      </Box>
    </Box>

    <Box sx={comparisonGridSx}>
      <Box sx={comparisonItemSx}>
        <Typography variant="text-md-semibold" sx={labelSx}>
          {strings.good}
        </Typography>
        <Typography variant="text-md-regular" sx={bodySx}>
          {prompt.good}
        </Typography>
      </Box>
      <Box sx={comparisonItemSx}>
        <Typography variant="text-md-semibold" sx={labelSx}>
          {strings.great}
        </Typography>
        <Typography variant="text-md-regular" sx={bodySx}>
          {prompt.great}
        </Typography>
      </Box>
    </Box>

    <Box sx={indentedContainerSx}>
      <Box sx={sectionSx}>
        <Typography variant="text-md-semibold" sx={labelSx}>
          {strings.quickTests}
        </Typography>
        {prompt.quickTests.map((test) => (
          <Box key={test} sx={testItemSx}>
            <AppIcon
              iconName="ArrowRight"
              diameter="16px"
              color="Colors/Foreground/fg-quaternary"
            />
            <Typography variant="text-md-regular" sx={bodySx}>
              {test}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={sectionSx}>
        <Typography variant="text-md-semibold" sx={labelSx}>
          {strings.riskToTake}
        </Typography>
        <Typography variant="text-md-regular" sx={bodySx}>
          {prompt.riskToTake}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const detailsOuterSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});

const detailsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  maxWidth: 600,
});

const indentedContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  maxWidth: 600,
  pl: backrs.spacing['spacing-xl'],
});

const promptCardSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  padding: backrs.spacing['spacing-xl'],
});

const promptTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  fontStyle: 'italic',
});

const comparisonGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: backrs.spacing['spacing-lg'],
  pl: backrs.spacing['spacing-xl'],
});

const comparisonItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const testItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
